import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, dp = 2) =>
  n == null ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });

function BookSide({ rows, color, alignRight }) {
  const maxVol = Math.max(...rows.map((r) => r[1]), 0.000001);
  return (
    <div className="space-y-1">
      {rows.map((r, i) => (
        <div key={i} className={`relative flex justify-between px-2 py-0.5 font-tabular text-[11px] ${alignRight ? "flex-row-reverse" : ""}`}>
          <span
            className={`absolute inset-y-0 ${alignRight ? "right-0" : "left-0"} ${color === "red" ? "bg-[#EF5350]/10" : "bg-[#26A69A]/10"}`}
            style={{ width: `${Math.min(100, (r[1] / maxVol) * 100)}%` }}
          />
          <span className={`relative ${color === "red" ? "text-[#EF5350]" : "text-[#26A69A]"}`}>{fmt(r[0])}</span>
          <span className="relative text-white/45">{fmt(r[1], 4)}</span>
        </div>
      ))}
    </div>
  );
}

export default function OrderBook({ symbol }) {
  const [book, setBook] = useState(null);
  const [trades, setTrades] = useState([]);

  useEffect(() => {
    if (!symbol) return;
    const load = () => {
      axios.get(`${API}/terminal/book/${symbol}`).then(({ data }) => setBook(data)).catch(() => {});
      axios.get(`${API}/terminal/trades/${symbol}`).then(({ data }) => setTrades(data.trades || [])).catch(() => {});
    };
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [symbol]);

  const asks = (book?.asks || []).slice(0, 10).reverse();
  const bids = (book?.bids || []).slice(0, 10);
  const spread = asks.length && bids.length ? asks[asks.length - 1][0] - bids[0][0] : null;

  return (
    <div className="flex flex-col gap-5" data-testid="order-book">
      <div className="border border-white/10 bg-ink">
        <p className="border-b border-white/10 px-4 py-2.5 text-[10px] uppercase tracking-[0.25em] text-white/35">
          Order book
        </p>
        <div className="grid grid-cols-2 gap-4 p-3">
          <div>
            <p className="mb-2 px-2 text-[9px] uppercase tracking-[0.2em] text-[#EF5350]/70">Asks</p>
            <BookSide rows={asks} color="red" />
          </div>
          <div>
            <p className="mb-2 px-2 text-[9px] uppercase tracking-[0.2em] text-[#26A69A]/70">Bids</p>
            <BookSide rows={bids} color="green" alignRight />
          </div>
        </div>
        <p className="border-t border-white/10 px-4 py-2 text-center font-tabular text-[10px] text-white/35" data-testid="book-spread">
          {spread != null ? `Spread $${fmt(spread)}` : "Connecting to book…"}
        </p>
      </div>

      <div className="border border-white/10 bg-ink">
        <p className="border-b border-white/10 px-4 py-2.5 text-[10px] uppercase tracking-[0.25em] text-white/35">
          Recent trades
        </p>
        <div className="max-h-56 space-y-1 overflow-y-auto p-3" data-testid="recent-trades">
          {trades.length === 0 && <p className="px-2 text-[11px] text-white/25">Connecting to feed…</p>}
          {trades.map((t, i) => (
            <div key={i} className="flex justify-between px-2 font-tabular text-[11px]">
              <span className={t.side === "buy" ? "text-[#26A69A]" : "text-[#EF5350]"}>${fmt(t.price)}</span>
              <span className="text-white/45">{fmt(t.qty, 5)}</span>
              <span className="text-white/25">{new Date(t.ts * 1000).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
